import React from "react";
import { Box, Tab, Tabs, Typography, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { LocationStatus, LOCATION_STATUS_STYLES } from "../../../../types/LocationStatus";
import { LocationTabConfig } from "./LocationTabConfig";
import LocationTabBadge from "./LocationTabBadge";

interface LocationTabBarProps {
  tabs: LocationTabConfig[];
  counts: Record<LocationStatus, number>;
  activeTab: LocationStatus;
  onTabChange: (variant: LocationStatus) => void;
}

/**
 * Container: an underline strip on SM+, a bordered box of four count tiles on mobile.
 */
const TabBarRoot = styled(Box)(({ theme }) => ({
  borderBottom: "2px solid #e0e4e8",
  marginBottom: "20px",
  [theme.breakpoints.down("sm")]: {
    border: "1px solid #e0e4e8",
    borderRadius: 8,
    overflow: "hidden",
    background: "#fff",
    marginBottom: 16,
  },
}));

const StyledTabs = styled(Tabs)({
  minHeight: "unset",
  "& .MuiTabs-indicator": {
    height: 3,
    backgroundColor: "#3f4fc1",
  },
});

const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: "unset",
  padding: "11px 20px",
  fontSize: "0.9643rem",   // 13.5px at 14px root
  fontWeight: 500,
  color: "#7a8a9a",
  textTransform: "none",
  whiteSpace: "nowrap",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "7px",
  "&.Mui-selected": {
    color: "#3f4fc1",
    fontWeight: 700,
  },
  "&:hover": {
    color: "#3f4fc1",
  },
  flex: 1,
  minWidth: "unset",
  // Mobile: four equal tiles, the count above the label (all fit, no scrolling)
  [theme.breakpoints.down("sm")]: {
    minHeight: 60,
    padding: "6px 2px",
    flexDirection: "column",
    gap: 0,
    fontSize: "0.8571rem", // 12px at 14px root
    "& + &": { borderLeft: "1px solid #eef1f4" },
    "&.Mui-selected": {
      backgroundColor: "rgba(63, 79, 193, 0.08)",
    },
  },
}));

/**
 * Renders the horizontal tab strip for the location list.
 *
 * - **Mobile** (`xs`): four equal count tiles — the count in its status color
 *   above a short label — so every status fits without scrolling.
 * - **SM+**: tabs stretch equally to fill the bar, with icon, label and badge.
 */
const LocationTabBar = ({ tabs, counts, activeTab, onTabChange }: LocationTabBarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <TabBarRoot>
      <StyledTabs
        value={activeTab}
        onChange={(_, value: LocationStatus) => onTabChange(value)}
        variant="fullWidth"
        aria-label="Location status tabs"
      >
        {tabs.map((tab) => (
          <StyledTab
            key={tab.variant}
            value={tab.variant}
            label={
              isMobile ? (
                <>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "1.2857rem", // 18px at 14px root
                      fontWeight: 700,
                      lineHeight: 1.3,
                      fontVariantNumeric: "tabular-nums",
                      color: LOCATION_STATUS_STYLES[tab.variant].color,
                    }}
                  >
                    {counts[tab.variant]}
                  </Typography>
                  {tab.label}
                </>
              ) : (
                <Box display="flex" alignItems="center" gap="7px">
                  {tab.icon}
                  {tab.label}
                  <LocationTabBadge
                    variant={tab.variant}
                    count={counts[tab.variant]}
                    isTabActive={activeTab === tab.variant}
                  />
                </Box>
              )
            }
            aria-label={`${tab.label} locations (${counts[tab.variant]})`}
          />
        ))}
      </StyledTabs>
    </TabBarRoot>
  );
};

export default LocationTabBar;
